/* eslint-disable react-hooks/exhaustive-deps */

import { useMediaQuery, useTheme } from "@material-ui/core";
import Config, { CLOSE_SIDE_BAR_GROUP_MEDICAL } from "app/appConfig";
import AppContext from "app/appContext";
import localStorageService from "app/services/localStorageService";
import { loadProgressBar } from "axios-progress-bar";
import history from "history.js";
import { observer } from "mobx-react";
import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { renderRoutes } from "react-router-config";
import { withRouter } from "react-router-dom";
import { convertMenuDtoToNavigation } from "utils";
import GlobitsBreadcrumb from "../GlobitsBreadcrumb";
import LayoutFooter from "./LayoutFooter";
import LayoutHeader from "./LayoutHeader";
import LayoutSidebar from "./LayoutSidebar";

export const LayoutContext = createContext();

function LayoutContainer() {
  const { t } = useTranslation();
  const { routes, menus } = useContext(AppContext);
  const { location } = history;

  const [router, setRouter] = useState([]);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [breadcrumbList, setBreadcrumbList] = useState([]);
  const [roles, setRoles] = useState(
    localStorageService.getLoginUser()?.roles?.map((item) => item.authority) ||
      []
  );
  const [user, setUser] = useState(null);
  const [openSidebar, setOpenSidebar] = useState(false);
  const [dynamicNavigation, setDynamicNavigation] = useState([]);

  const theme = useTheme();
  const isScreenSm = useMediaQuery(theme.breakpoints.down("sm"));

  const findMenuByPath = (menus, path) => {
    for (let m of menus) {
      if (m?.path === path) return m;
      if (m?.navigation) {
        const found = findMenuByPath(m.navigation, path);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (menus?.length > 0) {
      const formatted = convertMenuDtoToNavigation(menus);
      setDynamicNavigation(formatted);
    }
  }, []);
  useEffect(() => {
    if (!user) {
      const users = localStorageService.getItem("auth_user");
      if (users && users?.user) {
        setUser(users?.user);
        setRoles(users.user?.roles?.map((item) => item.authority) || []);
      } else if (users) {
        setUser(users);
        setRoles(users?.roles?.map((item) => item.authority) || []);
      }
    }
  }, [moduleInfo]);

  useEffect(() => {
    if (dynamicNavigation.length > 0) {
      const currentModule = dynamicNavigation.find(
        (e) =>
          location.pathname.includes(e.path) &&
          location.pathname[e.path.length + 1] === "/"
      );

      setModuleInfo(currentModule || null);
    }
  }, [location.pathname, dynamicNavigation]);

  useEffect(() => {
    if (routes) {
      setRouter(convertRouter(routes));
    }
  }, [routes, roles]);

  useEffect(() => {
    if (!moduleInfo) return;

    const url = location.pathname;
    const parts = url.split("/");
    const lastPart = parts[parts.length - 1];

    const rootName = { name: t(moduleInfo.name) };

    const second = findMenuByPath(moduleInfo.navigation || [], lastPart);

    if (!second) {
      setBreadcrumbList([rootName]);
      return;
    }

    const secondName = { name: t(second.name) };

    if (second.navigation) {
      const sub = second.navigation.find((x) => x.path === lastPart);
      if (sub) {
        setBreadcrumbList([rootName, secondName, { name: t(sub.name) }]);
        return;
      }
    }

    setBreadcrumbList([rootName, secondName]);
  }, [location.pathname, moduleInfo]);

  const convertRouter = (listRouter, rootPath = "") => {
    return listRouter.reduce((list, router) => {
      let is_Permission =
        !("auth" in router) || roles.some((role) => router.auth.includes(role));

      if (is_Permission) {
        const newRouter = { ...router };
        let path = rootPath;

        if (newRouter.path) {
          if (path) {
            path += "/" + newRouter.path;
          } else {
            path = Config.ROOT_PATH + (newRouter.path || "");
          }
        }

        if ("path" in newRouter) {
          newRouter.path = path;
        }

        if ("children" in newRouter && Array.isArray(newRouter.children)) {
          list = [...list, ...convertRouter(router.children, path)];
        } else {
          list.push(newRouter);
        }
      }
      return list;
    }, []);
  };

  const routesComponent = (routes) => {
    let newRoutes = [];
    routes?.map((item) => {
      if (!item.auth || roles?.some((role) => item.auth.indexOf(role) !== -1)) {
        if (item.children) {
          item.children.map((subItem) => {
            if (
              !subItem.auth ||
              roles?.some((role) => subItem.auth.indexOf(role) !== -1)
            ) {
              newRoutes.push(subItem);
            }
            return undefined;
          });
        } else {
          newRoutes.push(item);
        }
      }
      return undefined;
    });

    return renderRoutes(newRoutes);
  };

  if (moduleInfo) {
    loadProgressBar();
  }

  return (
    <LayoutContext.Provider value={{ isScreenSm, openSidebar, setOpenSidebar }}>
      <LayoutSidebar module={moduleInfo} user={user} />
      <section
        className="layout-container"
        onClick={() => {
          const isGroupMedicalExam =
            window.location.pathname.includes("group-medical-exam");

          if (
            openSidebar &&
            !(CLOSE_SIDE_BAR_GROUP_MEDICAL && isGroupMedicalExam)
          ) {
            setOpenSidebar(false);
          }
        }}
      >
        {useMemo(
          () => moduleInfo && <LayoutHeader module={moduleInfo} user={user} />,
          [moduleInfo, user]
        )}

        {useMemo(
          () => (
            <main className="content-container">
              {moduleInfo?.name ? (
                <div className="pb-1">
                  <GlobitsBreadcrumb routeSegments={breadcrumbList} />
                </div>
              ) : (
                <></>
              )}
              {router && router?.length > 0 ? routesComponent(router) : <></>}
            </main>
          ),
          [router, breadcrumbList]
        )}
        {useMemo(
          () => (
            <LayoutFooter />
          ),
          []
        )}
      </section>

      {openSidebar && isScreenSm && (
        <div
          className="overlay-sidebar-sm"
          onClick={() => setOpenSidebar(false)}
        ></div>
      )}
    </LayoutContext.Provider>
  );
}

export default memo(withRouter(observer(LayoutContainer)));
